import { OneWayDragger } from "./OneWayDragger";
import { DraggerType, type Point2D, type Point3D } from "./types";
import { PREVIEW_TEXT_STYLE } from "./previewStyles";
import type { NoteConfig } from "@/types";
import { insertTextEntity } from "./textInsertion";

/**
 * Dragger for placing text entities.
 *
 * Places MText (multi-line text) at the location where the user drags.
 * The text is positioned at the end point of the drag.
 *
 * MText is used instead of OdDbText to support:
 * - Multi-line text content
 * - Conversion to MLeader via AddLeaderHandle
 *
 * Usage:
 * - Set activeText before activating
 * - Drag to position the text
 * - Release to place the text entity
 *
 * Text properties:
 * - activeText: The text content to display
 * - textHeight: Height of the text in world units
 * - textRotation: Rotation angle in radians
 */
export class TextDragger extends OneWayDragger {
  readonly type = DraggerType.Text;

  /** Text content to insert */
  private _activeText = "Text";

  private previewTextId: string | null = null;

  // ============== Configuration ==============

  /**
   * Get the active text content.
   */
  get activeText(): string {
    return this._activeText;
  }

  /**
   * Set the active text content.
   */
  set activeText(text: string) {
    this._activeText = text || "Text";
  }

  // ============== Preview ==============

  protected createPreview(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    // Show text preview at cursor position
    this.previewTextId = overlay.createText(
      screen.x,
      screen.y,
      this._activeText,
      PREVIEW_TEXT_STYLE,
    );

    if (this.previewTextId) {
      this.addPreviewId(this.previewTextId);
    }
  }

  protected updatePreview(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay || !this.previewTextId) return;

    overlay.update(this.previewTextId, {
      x: screen.x,
      y: screen.y,
    });
  }

  // ============== Entity Creation ==============

  protected createEntity(_screen: Point2D, world: Point3D): void {
    // Get text height from NoteConfig or use dragger's default
    const noteConfig = this.viewer.activeNoteConfig;

    this.createAtPosition(
      world.x,
      world.y,
      this._activeText,
      noteConfig,
      this.config.autoSelect,
    );
  }

  // ============== Direct Creation ==============

  /**
   * Create a text entity at a specific world position.
   * Used for drag-and-drop from toolbar (NoteConfigButton).
   * @param worldX X coordinate in world units
   * @param worldY Y coordinate in world units
   * @param text Optional text content (defaults to activeText)
   * @param config: config containing properties to apply
   * @param isSelected control the selection at the end of the process
   * @returns true if the text was created successfully
   */
  createAtPosition(
    worldX: number,
    worldY: number,
    text: string,
    config: NoteConfig,
    isSelected = false,
  ) {
    insertTextEntity(this.viewer, this.module, {
      via: "shortcut_modal",
      worldX,
      worldY,
      text,
      config,
      select: isSelected,
      source: "TextDragger.createAtPosition",
    });
  }

  // ============== Override Minimum Drag ==============

  /**
   * Override to allow placing text with minimal movement.
   * Text can be placed with just a tap (no minimum drag required).
   */
  protected hasMinimumDrag(): boolean {
    // Allow placing with minimal movement (2 pixels)
    if (!this.startScreen || !this.currentScreen) {
      return false;
    }
    const dx = this.currentScreen.x - this.startScreen.x;
    const dy = this.currentScreen.y - this.startScreen.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance >= 2;
  }
}
